import React, { FC } from "react";

import {
  VictoryChart,
  VictoryTheme,
  VictoryLabel,
  VictoryAxis,
  VictoryGroup,
  VictoryLine,
  VictoryVoronoiContainer,
  VictoryTooltip,
  VictoryBar,
  VictoryLegend,
  VictoryPortal,
} from "victory";

export type ChartDatum = {
  x: string;
  y: number;
  source: string;
};

type RoiChartProps = {
  title: string;
  tickFormat: (tick: any) => string;
  data: Array<ChartDatum>[];
  timeIntervals: string[];
  vpdOnly?: boolean;
  showLegend?: boolean;
};

const vpdNonVpdColourScale = ["rgb(23,162,184)", "rgb(255,77,79)"];
const allMattersVpdColourScale = ["rgb(0,21,41)", "rgb(23,162,184)", "rgb(255,77,79)"];

export const RoiLineChart: FC<RoiChartProps> = ({
  title,
  tickFormat,
  data,
  timeIntervals,
  vpdOnly = false,
  showLegend = false,
}) => {
  // check if all elements in data are empty or invalid
  const allEmpty = data.every((dataSet) => !dataSet || dataSet.length === 0);
  if (allEmpty) {
    return (
      <VictoryChart theme={VictoryTheme.grayscale}>
        <VictoryLabel text={title} x={225} y={10} textAnchor="middle" />
        <VictoryAxis
          dependentAxis
          tickFormat={tickFormat}
          tickLabelComponent={<VictoryLabel style={{ fontSize: 10 }} />}
          style={{
            grid: { stroke: "rgb(192, 192, 192)", strokeWidth: 0.5 },
          }}
        />
        <VictoryAxis
          tickValues={timeIntervals}
          tickLabelComponent={
            <VictoryLabel dx={-0} dy={0} angle={-45} textAnchor="end" style={{ fontSize: 10 }} />
          }
        />
        <VictoryLabel
          text="Not enough data"
          x={225}
          y={150}
          textAnchor="middle"
          style={{ fontSize: 20 }}
        />
      </VictoryChart>
    );
  }

  // Filter valid datasets once
  const validDatasets = data.filter((dataSet) => dataSet && dataSet.length > 0);

  const allYValues = validDatasets.flat().map((datum) => datum.y);
  const minY = Math.min(...allYValues);
  const maxY = Math.max(...allYValues);
  const range = maxY - minY;
  const padding = range * 0.1;
  const paddedMinY = Math.max(0, minY - padding);
  const paddedMaxY = maxY + padding;
  const closenessThreshold = range * 0.1;

  // Get all last points from valid datasets
  const lastPoints = validDatasets.map((dataSet) => dataSet[dataSet.length - 1]);

  // Calculate offsets for all last points
  const offsets = lastPoints.map((point, index) => {
    const closePoints = lastPoints.filter(
      (p, i) => i !== index && Math.abs(p.y - point.y) < closenessThreshold
    );
    if (closePoints.length === 0) return 0;
    // Assign alternating offsets based on relative position
    const position = closePoints.reduce((sum, p) => sum + (p.y < point.y ? -1 : 1), 0);
    return position > 0 ? 10 : -10;
  });

  return (
    <VictoryChart
      theme={VictoryTheme.grayscale}
      padding={{ top: 50, bottom: 50, left: 50, right: showLegend ? 230 : 70 }}
      width={showLegend ? 720 : 570}
      height={400}
    >
      <VictoryLabel text={title} x={280} y={10} textAnchor="middle" />
      <VictoryAxis
        dependentAxis
        domain={{ y: [paddedMinY, paddedMaxY] }}
        tickFormat={tickFormat}
        tickLabelComponent={<VictoryLabel style={{ fontSize: 10 }} />}
        style={{
          grid: { stroke: "rgb(192, 192, 192)", strokeWidth: 0.5 },
        }}
      />
      <VictoryAxis
        tickValues={timeIntervals}
        tickLabelComponent={
          <VictoryLabel dx={-0} dy={0} angle={-45} textAnchor="end" style={{ fontSize: 10 }} />
        }
      />
      <VictoryGroup colorScale={vpdOnly ? vpdNonVpdColourScale.slice(0) : vpdNonVpdColourScale}>
        {validDatasets.map((dataSet, index) => {
          const lastPoint = dataSet[dataSet.length - 1];
          // Find original index for correct color
          const originalIndex = data.indexOf(dataSet);

          return (
            <VictoryLine
              key={originalIndex}
              interpolation={"linear"}
              data={dataSet}
              style={{ data: { strokeWidth: 4 } }}
              labels={({ datum }) =>
                datum.x === lastPoint.x ? tickFormat(Math.round(datum.y)) : null
              }
              labelComponent={
                <VictoryPortal>
                  <VictoryLabel
                    dx={20}
                    dy={offsets[index]}
                    textAnchor="start"
                    style={{
                      fontSize: 12,
                      fill: vpdNonVpdColourScale[originalIndex],
                    }}
                  />
                </VictoryPortal>
              }
            />
          );
        })}
      </VictoryGroup>
      {showLegend && (
        <VictoryLegend
          x={570}
          y={150}
          orientation="vertical"
          gutter={20}
          data={
            vpdOnly
              ? [
                  {
                    name: "VPD matters",
                    symbol: { fill: vpdNonVpdColourScale[0], type: "square" as const },
                  },
                ]
              : [
                  {
                    name: "VPD matters",
                    symbol: { fill: vpdNonVpdColourScale[0], type: "square" as const },
                  },
                  {
                    name: "Non-VPD matters",
                    symbol: { fill: vpdNonVpdColourScale[1], type: "square" as const },
                  },
                ]
          }
          titleComponent={<></>}
        />
      )}
    </VictoryChart>
  );
};

export const RoiBarChart: FC<RoiChartProps> = ({ title, tickFormat, data, timeIntervals }) => {
  // Build legend data based on what's actually being rendered
  const legendData = [];
  if (data[0] && data[0].length > 0) {
    legendData.push({
      name: "All matters",
      symbol: { fill: allMattersVpdColourScale[0], type: "square" as const },
    });
  }
  if (data[1] && data[1].length > 0) {
    legendData.push({
      name: "VPD matters",
      symbol: { fill: allMattersVpdColourScale[1], type: "square" as const },
    });
  }

  return (
    <VictoryChart
      width={550}
      height={400}
      domainPadding={20}
      containerComponent={
        <VictoryVoronoiContainer
          mouseFollowTooltips
          voronoiDimension="x"
          labels={({ datum }: { datum: ChartDatum }) =>
            `${datum.source === "external" ? "All matters" : "VPD matters"}: ${datum.y}`
          }
          labelComponent={<VictoryTooltip centerOffset={{ y: -50 }} />}
        />
      }
    >
      <VictoryLabel text={title} textAnchor="middle" x={280} y={10} />
      <VictoryAxis
        dependentAxis
        tickFormat={tickFormat}
        tickLabelComponent={<VictoryLabel style={{ fontSize: 10 }} />}
        style={{
          grid: { stroke: "rgb(192, 192, 192)", strokeWidth: 0.5 },
        }}
      />
      <VictoryAxis
        tickValues={timeIntervals}
        tickLabelComponent={
          <VictoryLabel dx={-0} dy={0} angle={-45} textAnchor="end" style={{ fontSize: 10 }} />
        }
      />
      <VictoryGroup colorScale={allMattersVpdColourScale}>
        {data[0] && data[0].length > 0 && <VictoryBar data={data[0]} />}
        {data[1] && data[1].length > 0 && <VictoryBar data={data[1]} />}
      </VictoryGroup>
      {legendData.length > 0 && (
        <VictoryLegend
          x={170}
          y={20}
          orientation="horizontal"
          gutter={20}
          data={legendData}
          titleComponent={<></>}
        />
      )}
    </VictoryChart>
  );
};
